import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Edit2, Save, X, Upload, CheckCircle, Mail, Phone, MapPin, 
  Briefcase, Award, TrendingUp, FileText, User, ExternalLink,
  Loader2, Target, BarChart3
} from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import api from '@/lib/api';
import ResumeUploadModal from './ResumeUploadModal';
import LinkedInScoreModal from './LinkedInScoreModal';
import ScoreCard from '../ScoreCard';
import VerificationQuestions from '../VerificationQuestions';
import { ScoringSection } from '../ScoringSection';
import { ScreeningSection } from '../ScreeningSection';
import V4ScoreReportModal from './V4ScoreReportModal';
import EnrichContactPanel from '../EnrichContactPanel';

const getScreeningStatusMeta = (status) => {
  switch (status) {
    case "pending":
      return { label: "Scheduled", className: "bg-blue-50 text-blue-700" };
    case "in_progress":
      return { label: "In progress", className: "bg-amber-50 text-amber-700" };
    case "completed":
      return { label: "Completed", className: "bg-emerald-50 text-emerald-700" };
    case "no_answer":
      return { label: "No answer", className: "bg-rose-50 text-rose-700" };
    case "handoff":
      return { label: "Human handoff", className: "bg-violet-50 text-violet-700" };
    default:
      return { label: "Not started", className: "bg-slate-100 text-slate-600" };
  }
};

const getCallStatusMeta = (status) => {
  switch (status) {
    case "active":
      return { label: "In progress", className: "bg-amber-50 text-amber-700" };
    case "completed":
      return { label: "Completed", className: "bg-emerald-50 text-emerald-700" };
    case "handoff":
      return { label: "Human handoff", className: "bg-violet-50 text-violet-700" };
    case "failed":
      return { label: "Failed", className: "bg-rose-50 text-rose-700" };
    default:
      return { label: status || "Unknown", className: "bg-slate-100 text-slate-600" };
  }
};

export default function CandidateDetailPage() {
  const { candidateId } = useParams();
  const navigate = useNavigate();
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState({});
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [showLinkedInScoreModal, setShowLinkedInScoreModal] = useState(false);
  const [jobData, setJobData] = useState(null);
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [job, setJob] = useState(null);
  const [activeTab, setActiveTab] = useState('linkedin');
  const [v4ScoreModalSource, setV4ScoreModalSource] = useState(null); // "linkedin" | "resume"
  const [rescoreLoading, setRescoreLoading] = useState(false);
  const [voiceModal, setVoiceModal] = useState(false);
  const [voicePhone, setVoicePhone] = useState("");
  const [voiceLoading, setVoiceLoading] = useState(false);
  const [screeningStatus, setScreeningStatus] = useState("not_started");
  const [screeningStatusLoading, setScreeningStatusLoading] = useState(false);
  const [callHistory, setCallHistory] = useState([]);
  const [callHistoryLoading, setCallHistoryLoading] = useState(false);
  const { addToast } = useToast();

  useEffect(() => {
    fetchCandidate();
  }, [candidateId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCandidate = async () => {
    try {
      const { data } = await api.get(`/candidates/${candidateId}/detail`);
      setCandidate(data);
      
      // Fetch job for score modal
      if (data.job_id) {
        const { data: jobData } = await api.get(`/jobs/${data.job_id}`);
        setJob(jobData);
      }
      
      // Initialize edit values
      setEditValues({
        name: data.name || '',
        email: data.email || '',
        phone: data.phone || '',
        notes: data.notes || ''
      });
    } catch (err) {
      addToast('Failed to load candidate', 'error');
      navigate('/candidates');
    } finally {
      setLoading(false);
    }
  };

  const handleEditField = async (field, value) => {
    setSaving(true);
    try {
      await api.put(`/candidates/${candidateId}/edit-field`, {
        field_name: field,
        new_value: value,
        old_value: candidate[field]
      });
      setCandidate({ ...candidate, [field]: value });
      setEditMode({ ...editMode, [field]: false });
      addToast('Field updated', 'success');
    } catch (err) {
      addToast('Failed to update field', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleResumeUploaded = () => {
    setShowResumeModal(false);
    fetchCandidate();
    addToast('Resume scored successfully!', 'success');
  };

  const handleRescoreLinkedIn = async () => {
    if (!candidate?.candidate_id) return;
    try {
      setRescoreLoading(true);
      const { data } = await api.post(`/candidates/${candidate.candidate_id}/rescore-linkedin`);
      setCandidate(data);
      addToast('LinkedIn score updated!', 'success');
    } catch (err) {
      logger.error('Rescore LinkedIn failed', err);
      addToast('LinkedIn re-scoring failed. Please try again.', 'error');
    } finally {
      setRescoreLoading(false);
    }
  };

  const handleRescore = async () => {
    try {
      await api.post(`/candidates/${candidateId}/score-resume`);
      fetchCandidate();
      addToast('Candidate scored successfully.', 'success');
    } catch {
      addToast('Failed to score candidate.', 'error');
    }
  };

  const handleTriggerVoiceCall = async () => {
    setVoiceLoading(true);
    try {
      await api.post("/voice/trigger-call", {
        candidate_id: candidate.candidate_id,
        job_id: candidate.job_id,
        workspace_id: candidate.workspace_id,
        phone_override: voicePhone || undefined,
      });
      addToast(
        `AI Screening call triggered for ${candidate?.name}`,
        "success"
      );
      setVoiceModal(false);
      setVoicePhone("");
    } catch (err) {
      addToast(
        err?.response?.data?.detail || "Failed to trigger call",
        "error"
      );
    } finally {
      setVoiceLoading(false);
    }
  };

  useEffect(() => {
    if (!candidate?.candidate_id) return;

    let isCancelled = false;
    let intervalId = null;

    const TERMINAL = new Set([
      "completed", "no_answer", "handoff", "not_started",
    ]);

    const fetchStatus = async () => {
      setScreeningStatusLoading(true);
      try {
        const res = await api.get(
          `/voice/screening-status/${candidate.candidate_id}`
        );
        if (isCancelled) return null;
        const status = res.data.status || "not_started";
        setScreeningStatus(status);
        return status;
      } catch {
        if (!isCancelled) setScreeningStatus("not_started");
        return "not_started";
      } finally {
        if (!isCancelled) setScreeningStatusLoading(false);
      }
    };

    const startPolling = async () => {
      const initial = await fetchStatus();

      if (isCancelled) return;

      // Start 10s polling only if status is active
      if (initial && !TERMINAL.has(initial)) {
        intervalId = setInterval(async () => {
          const updated = await fetchStatus();
          if (isCancelled && intervalId) {
            clearInterval(intervalId);
            return;
          }
          // Stop when terminal
          if (updated && TERMINAL.has(updated) && intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
        }, 10000);
      }
    };

    startPolling();

    return () => {
      isCancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [candidate?.candidate_id]);

  useEffect(() => {
    if (!candidate?.candidate_id) return;

    let isCancelled = false;

    const fetchHistory = async () => {
      setCallHistoryLoading(true);
      try {
        const res = await api.get(
          `/voice/call-history/${candidate.candidate_id}`
        );
        if (isCancelled) return;
        setCallHistory(res.data.items || []);
      } catch {
        if (!isCancelled) setCallHistory([]);
      } finally {
        if (!isCancelled) setCallHistoryLoading(false);
      }
    };

    fetchHistory();

    return () => { isCancelled = true; };
  }, [candidate?.candidate_id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <Loader2 className="w-8 h-8 text-[#2563eb] animate-spin" />
      </div>
    );
  }

  if (!candidate) return null;

  // ── Score normalization (mirrors CandidateTable logic) ───────────────────
  const getDisplayScore = (raw) =>
    typeof raw === 'number' && !Number.isNaN(raw) && raw > 0 ? raw : null;

  const linkedinScore =
    getDisplayScore(candidate.linkedinscore?.score) ??
    getDisplayScore(candidate.linkedin_score?.score) ??
    getDisplayScore(candidate.relevancescore) ??
    getDisplayScore(candidate.relevance_score) ??
    null;

  const rawResumeScore = candidate.resumescore || candidate.resume_score || candidate.resumeScore || null;
  const resumeScore =
    typeof rawResumeScore === 'object'
      ? (rawResumeScore?.score != null ? rawResumeScore.score : null)
      : (typeof rawResumeScore === 'number' ? rawResumeScore : null);

  const hasAnyScore =
    linkedinScore != null ||
    resumeScore != null ||
    !!candidate.score_breakdown ||
    !!candidate.resume_score_breakdown ||
    !!candidate.scorebreakdown ||
    !!candidate.resumescorebreakdown;
  const hasResume = !!candidate.resume_uploaded_at;
  const structured = candidate.exa_structured || {};
  const sourcedData = candidate.sourced_data || {};

  // ── CallHistoryList subcomponent (defined here to close over navigate) ───
  const CallHistoryList = ({ items, compact = false }) => {
    if (!items || items.length === 0) {
      return (
        <p className="text-xs text-slate-500">
          No AI screening calls yet for this candidate.
        </p>
      );
    }

    const displayItems = compact ? items.slice(0, 2) : items;

    return (
      <ul className="divide-y divide-slate-100">
        {displayItems.map((item) => {
          const { label, className } = getCallStatusMeta(item.status);
          const started = item.started_at ? new Date(item.started_at) : null;
          const duration =
            item.duration_seconds != null
              ? `${Math.round(item.duration_seconds / 60)} min`
              : "-";

          return (
            <li
              key={item.callsession_id}
              className="py-3 flex items-start justify-between gap-4"
              data-testid={`call-history-item-${item.callsession_id}`}
            >
              <div className="min-w-0 flex-1">
                {/* Status pill */}
                <div className="flex items-center gap-2">
                  <span
                    className={
                      "inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold " +
                      className
                    }
                  >
                    {label}
                  </span>
                </div>

                {/* Scores line */}
                <div className="mt-0.5 text-[11px] text-slate-600">
                  {typeof item.overall_score === "number" && (
                    <>
                      Score: {item.overall_score}
                      {(typeof item.technical_score === "number" ||
                        typeof item.communication_score === "number" ||
                        typeof item.culture_score === "number") && (
                        <>
                          {" ("}
                          {typeof item.technical_score === "number" &&
                            `Tech ${item.technical_score}`}
                          {typeof item.communication_score === "number" &&
                            `${typeof item.technical_score === "number" ? ", " : ""}Comm ${item.communication_score}`}
                          {typeof item.culture_score === "number" &&
                            `${(typeof item.technical_score === "number" ||
                              typeof item.communication_score === "number")
                              ? ", "
                              : ""}Culture ${item.culture_score}`}
                          {")"}
                        </>
                      )}
                    </>
                  )}
                </div>

                {/* Proctoring chip */}
                {(() => {
                  const hasIssues = (item.strike_count || 0) > 0;
                  const reasons = (item.strike_reasons || []).join(", ");
                  return (
                    <div className="mt-1 text-[11px]">
                      <span
                        className={
                          "inline-flex items-center px-2 py-0.5 rounded-full font-semibold " +
                          (hasIssues
                            ? "bg-rose-50 text-rose-700"
                            : "bg-emerald-50 text-emerald-700")
                        }
                        data-testid={`proctoring-chip-${item.callsession_id}`}
                      >
                        {hasIssues
                          ? `Proctoring: ${item.strike_count} strike${item.strike_count > 1 ? "s" : ""}`
                          : "Proctoring: Clean"}
                      </span>
                      {hasIssues && reasons && (
                        <span className="ml-2 text-slate-500">{reasons}</span>
                      )}
                    </div>
                  );
                })()}

                {/* Timestamp / duration */}
                <div className="mt-1 text-xs text-slate-500">
                  {started
                    ? started.toLocaleString()
                    : "Start time unknown"}
                  {" · "}
                  Duration: {duration}
                </div>

                {/* Report summary */}
                {item.report_summary && (
                  <div className="mt-1 text-xs text-slate-500 line-clamp-2">
                    Report: {item.report_summary}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() =>
                    navigate(`/voice/calls/${item.callsession_id}`)
                  }
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl
                             bg-slate-50 text-slate-700 hover:bg-slate-100
                             text-xs font-semibold transition-colors"
                  data-testid={`view-call-report-${item.callsession_id}`}
                >
                  View report
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className="flex-1 overflow-auto bg-transparent p-6 sm:p-8 relative" style={{ fontFamily: 'Inter, sans-serif' }} data-testid="candidate-detail-page">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <button
          onClick={() => navigate('/candidates')}
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-blue-600 bg-white/50 backdrop-blur-md px-4 py-2 rounded-xl border border-slate-200/50 hover:bg-white hover:shadow-md transition-all w-fit mb-6 shadow-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Back to Candidates</span>
        </button>

        {/* Candidate Header */}
        <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-xl border border-slate-200/60 p-6 sm:p-8 mb-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-bl from-blue-100/50 via-emerald-50/20 to-transparent rounded-full blur-3xl pointer-events-none -z-10 translate-x-1/4 -translate-y-1/4" />
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-6">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-emerald-50 rounded-2xl border-2 border-white shadow-lg flex items-center justify-center flex-shrink-0 text-2xl font-bold text-blue-700">
                {candidate.name?.charAt(0)?.toUpperCase() || 'C'}
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-black text-slate-900 tracking-tight">{candidate.name}</h1>
                  {(() => {
                    const { label: screeningLabel, className: screeningClass } = getScreeningStatusMeta(screeningStatus);
                    return (
                      <span
                        className={"inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold " + screeningClass}
                        data-testid="screening-status-badge"
                      >
                        {screeningStatusLoading ? "Checking..." : `Screening: ${screeningLabel}`}
                      </span>
                    );
                  })()}
                </div>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  {(candidate.openToWork || candidate.open_to_work) && (
                    <span
                      className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700 border border-emerald-300"
                      title={candidate.otwSignalsFound?.join(', ') || 'Open to Work'}
                      data-testid="candidate-otw-badge"
                    >
                      🟢 Open to Work
                      {typeof candidate.otwConfidence === 'number' && candidate.otwConfidence > 0 && (
                        <span className="text-[10px] text-emerald-600">({candidate.otwConfidence})</span>
                      )}
                    </span>
                  )}
                </div>
                <p className="text-slate-600 mb-3">{sourcedData.current_title || structured.current_title || 'No title'}</p>
                <div className="mt-6 flex flex-wrap gap-4 p-4 bg-slate-50/80 rounded-2xl border border-slate-200/50 shadow-inner text-sm font-semibold text-slate-700">
                  {sourcedData.current_company && (
                    <div className="flex items-center gap-1.5">
                      <Briefcase className="w-4 h-4" />
                      {sourcedData.current_company}
                    </div>
                  )}
                  {sourcedData.us_location && (
                    <div className="flex items-center gap-1.5">
                      <MapPin className="w-4 h-4" />
                      {sourcedData.us_location}
                    </div>
                  )}
                  {candidate.open_to_work && (
                    <div className="flex items-center gap-1.5 text-green-600 font-medium">
                      <CheckCircle className="w-4 h-4" />
                      Open to Work
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setVoicePhone(candidate?.phone || "");
                  setVoiceModal(true);
                }}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl
                           bg-gradient-to-r from-violet-600 to-purple-600 text-white
                           font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5
                           transition-all"
                data-testid="ai-screen-btn"
              >
                <Phone className="w-4 h-4" />
                AI Screen
              </button>
              <button
                onClick={() => setShowResumeModal(true)}
                className="bg-gradient-to-r from-blue-600 to-emerald-600 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all text-white font-bold px-6 py-2.5 rounded-xl flex items-center gap-2"
                data-testid="upload-resume-btn"
              >
                <Upload className="w-4 h-4" />
                {hasResume ? 'Replace Resume' : 'Upload Resume'}
              </button>
            </div>
          </div>
        </div>

        {/* Phase 8 — SalesQL contact enrichment panel */}
        <EnrichContactPanel
          candidateId={candidateId}
          jobId={candidate.job_id || null}
        />

        {/* V4 Scoring & Reports — always shown for all candidates */}
        <ScoringSection
          candidate={candidate}
          onOpenScoreReport={(source) => setV4ScoreModalSource(source)}
          onUploadResume={() => setShowResumeModal(true)}
          onRescoreResume={handleRescore}
          onRescoreLinkedIn={handleRescoreLinkedIn}
          rescoreLinkedInLoading={rescoreLoading}
        />

        {/* Verification Questions Panel — SECOND (swapped down) */}
        <VerificationQuestions
          candidateId={candidateId}
          jobId={candidate.job_id}
          initialData={candidate.verification_data || null}
          initialScore={candidate.linkedinscore?.score ?? null}
          initialBreakdown={candidate.score_breakdown || null}
          initialFlags={candidate.score_flags || null}
        />

        {/* Screening section: AI questions + voice score */}
        <ScreeningSection candidate={candidate} />

        {/* Main Content Grid — tabs: Overview / Call History */}
        <div className="flex gap-1 border-b border-slate-200 mb-6">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2.5 text-sm font-semibold transition-colors ${
              activeTab !== 'callHistory'
                ? 'text-[#2563eb] border-b-2 border-[#2563eb]'
                : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid="overview-tab-btn"
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('callHistory')}
            className={`px-4 py-2.5 text-sm font-semibold transition-colors ${
              activeTab === 'callHistory'
                ? 'text-[#2563eb] border-b-2 border-[#2563eb]'
                : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid="call-history-tab-btn"
          >
            Call History
            {callHistory.length > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold">
                {callHistory.length}
              </span>
            )}
          </button>
        </div>

        {activeTab !== 'callHistory' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Editable Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact Information */}
            <div className="bg-white/80 backdrop-blur-sm border border-slate-200/50 rounded-2xl p-6 shadow-lg">
              <h2 className="text-lg font-bold text-slate-800 mb-4">Contact Information</h2>
              <div className="space-y-4">
                <EditableField
                  label="Email"
                  field="email"
                  value={editValues.email}
                  icon={Mail}
                  editMode={editMode.email}
                  onEdit={() => setEditMode({ ...editMode, email: true })}
                  onChange={(v) => setEditValues({ ...editValues, email: v })}
                  onSave={() => handleEditField('email', editValues.email)}
                  onCancel={() => {
                    setEditMode({ ...editMode, email: false });
                    setEditValues({ ...editValues, email: candidate.email });
                  }}
                />
                <EditableField
                  label="Phone"
                  field="phone"
                  value={editValues.phone}
                  icon={Phone}
                  editMode={editMode.phone}
                  onEdit={() => setEditMode({ ...editMode, phone: true })}
                  onChange={(v) => setEditValues({ ...editValues, phone: v })}
                  onSave={() => handleEditField('phone', editValues.phone)}
                  onCancel={() => {
                    setEditMode({ ...editMode, phone: false });
                    setEditValues({ ...editValues, phone: candidate.phone });
                  }}
                />
              </div>
            </div>

            {/* Profile Highlights - REMOVED per user request */}
            {/* Profile Summary - REMOVED per user request */}

            {/* Notes */}
            <div className="bg-white/80 backdrop-blur-sm border border-slate-200/50 rounded-2xl p-6 shadow-lg">
              <h2 className="text-lg font-bold text-slate-800 mb-4">Recruiter Notes</h2>
              {editMode.notes ? (
                <div>
                  <textarea
                    value={editValues.notes}
                    onChange={(e) => setEditValues({ ...editValues, notes: e.target.value })}
                    className="w-full border border-slate-200 rounded-lg p-3 text-sm text-slate-700 focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 outline-none"
                    rows={4}
                    placeholder="Add notes about this candidate..."
                  />
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => handleEditField('notes', editValues.notes)}
                      className="px-3 py-1.5 bg-[#2563eb] text-white rounded-lg text-xs font-medium hover:bg-[#1d4ed8] transition-colors"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setEditMode({ ...editMode, notes: false });
                        setEditValues({ ...editValues, notes: candidate.notes || '' });
                      }}
                      className="px-3 py-1.5 bg-slate-100 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-200 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div onClick={() => setEditMode({ ...editMode, notes: true })} className="cursor-pointer group">
                  {candidate.notes ? (
                    <p className="text-sm text-slate-700 leading-relaxed group-hover:text-slate-900">{candidate.notes}</p>
                  ) : (
                    <p className="text-sm text-slate-400 italic group-hover:text-slate-600">Click to add notes...</p>
                  )}
                </div>
              )}
            </div>

            {/* Recent AI Calls — inline compact */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-slate-800">
                  Recent AI Screening Calls
                </h3>
                {callHistory.length > 2 && (
                  <button
                    type="button"
                    onClick={() => setActiveTab('callHistory')}
                    className="text-xs font-semibold text-violet-600 hover:text-violet-700"
                    data-testid="view-all-calls-btn"
                  >
                    View all
                  </button>
                )}
              </div>
              {callHistoryLoading ? (
                <p className="text-xs text-slate-500">Loading call history...</p>
              ) : (
                <CallHistoryList items={callHistory} compact />
              )}
            </section>
          </div>

          {/* Right Column - Quick Info */}
          <div className="space-y-6">
            {/* Skills */}
            {sourcedData.skills?.length > 0 && (
              <div className="bg-white/80 backdrop-blur-sm border border-slate-200/50 rounded-2xl p-6 shadow-lg">
                <h3 className="text-sm font-bold text-slate-800 mb-3">Skills</h3>
                <div className="flex flex-wrap gap-2">
                  {sourcedData.skills.map((skill, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-blue-50 text-[#2563eb] rounded-full text-xs font-medium border border-blue-100"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Stats */}
            <div className="bg-white/80 backdrop-blur-sm border border-slate-200/50 rounded-2xl p-6 shadow-lg">
              <h3 className="text-sm font-bold text-slate-800 mb-4">Quick Info</h3>
              <div className="space-y-3">
                {sourcedData.years_experience && (
                  <div className="flex items-center gap-3">
                    <Award className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Experience</p>
                      <p className="text-sm font-medium text-slate-800">{sourcedData.years_experience} years</p>
                    </div>
                  </div>
                )}
                {sourcedData.seniority_level && (
                  <div className="flex items-center gap-3">
                    <TrendingUp className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Level</p>
                      <p className="text-sm font-medium text-slate-800 capitalize">{sourcedData.seniority_level}</p>
                    </div>
                  </div>
                )}
                {candidate.source_profile_url && (
                  <a
                    href={candidate.source_profile_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-[#2563eb] hover:text-[#1d4ed8] text-sm font-medium transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View LinkedIn Profile
                  </a>
                )}
              </div>
            </div>

            {/* Related Candidates */}
            {candidate.related_candidates?.length > 0 && (
              <div className="bg-white/80 backdrop-blur-sm border border-slate-200/50 rounded-2xl p-6 shadow-lg">
                <h3 className="text-sm font-bold text-slate-800 mb-4">Related Candidates</h3>
                <div className="space-y-3">
                  {candidate.related_candidates.map((related) => (
                    <div
                      key={related.candidate_id}
                      onClick={() => navigate(`/candidates/${related.candidate_id}`)}
                      className="flex items-center justify-between p-3 bg-slate-50/80 rounded-xl cursor-pointer hover:bg-slate-100/80 transition-colors border border-slate-100"
                    >
                      <div>
                        <p className="text-sm font-medium text-slate-800">{related.name}</p>
                        <p className="text-xs text-slate-500">{related.sourced_data?.current_title}</p>
                      </div>
                      <div className="text-xs font-semibold text-[#2563eb]">{related.linkedinscore?.score ?? '—'}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        )}

        {/* Call History tab */}
        {activeTab === 'callHistory' && (
          <div className="bg-white/80 backdrop-blur-sm border border-slate-200/50 rounded-2xl p-6 shadow-lg mt-4">
            <h2 className="text-lg font-bold text-slate-800 mb-4">Call History</h2>
            {callHistoryLoading ? (
              <p className="text-xs text-slate-500">Loading call history...</p>
            ) : (
              <CallHistoryList items={callHistory} />
            )}
          </div>
        )}
      </div>

      {showResumeModal && (
        <ResumeUploadModal
          candidateId={candidateId}
          onClose={() => setShowResumeModal(false)}
          onSuccess={handleResumeUploaded}
        />
      )}
      
      {showLinkedInScoreModal && jobData && (
        <LinkedInScoreModal
          candidate={candidate}
          job={jobData}
          onClose={() => setShowLinkedInScoreModal(false)}
        />
      )}

      {v4ScoreModalSource && candidate && (
        <V4ScoreReportModal
          candidate={candidate}
          source={v4ScoreModalSource}
          onClose={() => setV4ScoreModalSource(null)}
        />
      )}

      {voiceModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50
                        flex items-center justify-center p-4
                        animate-in fade-in duration-200">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl
                          border border-slate-200/60 w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-1">
              Trigger AI Screening Call
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              The AI will call{" "}
              <span className="font-semibold text-slate-700">
                {candidate?.name}
              </span>{" "}
              immediately and conduct a structured screening interview.
            </p>

            <label className="block text-xs font-semibold text-slate-600 mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              value={voicePhone}
              onChange={(e) => setVoicePhone(e.target.value)}
              placeholder="+1 (555) 000-0000"
              className="w-full px-4 py-3 rounded-xl border border-slate-200/70
                         bg-slate-50/50 focus:ring-2 focus:ring-purple-500
                         outline-none text-slate-900 transition-all mb-4"
              data-testid="voice-modal-phone-input"
            />

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setVoiceModal(false); setVoicePhone(""); }}
                className="px-4 py-2 rounded-xl bg-slate-100 text-slate-600
                           hover:bg-slate-200 font-semibold transition-colors"
                data-testid="voice-modal-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleTriggerVoiceCall}
                disabled={voiceLoading || (!voicePhone && !candidate?.phone)}
                className="px-5 py-2 rounded-xl bg-gradient-to-r from-violet-600
                           to-purple-600 text-white font-semibold shadow-md
                           hover:shadow-lg transition-all disabled:opacity-50
                           disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="voice-modal-confirm-btn"
              >
                {voiceLoading ? "Triggering..." : "Start AI Call"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EditableField({ label, field, value, icon: Icon, editMode, onEdit, onChange, onSave, onCancel }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-3 flex-1">
        <Icon className="w-4 h-4 text-slate-400" />
        <div className="flex-1">
          <label className="text-xs text-slate-500 block mb-1">{label}</label>
          {editMode ? (
            <input
              type="text"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="w-full border border-[#2563eb] rounded-lg px-3 py-1.5 text-sm text-slate-800 focus:ring-2 focus:ring-[#2563eb]/20 outline-none"
              autoFocus
            />
          ) : (
            <p className="text-sm text-slate-800 font-medium">{value || 'Not provided'}</p>
          )}
        </div>
      </div>
      {editMode ? (
        <div className="flex gap-1 ml-3">
          <button
            onClick={onSave}
            className="p-1.5 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
          >
            <Save className="w-4 h-4" />
          </button>
          <button
            onClick={onCancel}
            className="p-1.5 bg-slate-50 text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <button
          onClick={onEdit}
          className="p-1.5 text-slate-300 hover:text-[#2563eb] hover:bg-blue-50 rounded-lg transition-colors"
        >
          <Edit2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
